import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Tab, Table, Tabs } from 'react-bootstrap';
import { fetchPaylocityJobs } from './paylocityRequests';
import { toPaylocityJob } from './timeComparison';
import { timeUtilization } from './timeUtilization';
import { compareEngineeringShipDates, engineeringGroup, groupEngineeringShipWeeks, projectValue, summarizeEngineeringJob } from './engineeringTimeOverviewUtils';

const format = value => value == null ? '—' : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
const formatMargin = value => {
  if (value == null || value === '') return '—';
  const number = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(number) ? `${number.toFixed(1)}%` : '—';
};

function UtilizationCell({ allocated, actual }) {
  const { label, level } = timeUtilization(allocated, actual);
  const highlighted = level === 'over' || level === 'near';
  return <td className="text-end">
    <span className={highlighted ? 'badge' : ''}
      style={highlighted ? { color: level === 'over' ? '#b02a37' : '#a64b00', backgroundColor: level === 'over' ? '#f8d7da' : '#fff3e0' } : undefined}
      title={level === 'over' ? 'Over allocated hours' : level === 'near' ? 'At least 90% of allocated hours used' : 'Actual hours divided by allocated hours'}>
      {level === 'over' ? '⚑ ' : ''}{label}
    </span>
  </td>;
}

export default function EngineeringTimeOverview({ projects, projectLoading, projectError, endpoint, onRefreshProjects, onSelectJob, children }) {
  const [tab, setTab] = useState('progress');
  const [search, setSearch] = useState('');
  const [revision, setRevision] = useState(0);
  const jobsKey = JSON.stringify([...new Set(projects
    .filter(project => engineeringGroup(project) === tab)
    .map(project => String(projectValue(project, 'JobNumber') || '').trim())
    .filter(number => /^\d{7}$/.test(number))
    .map(toPaylocityJob))].sort());
  const scope = JSON.stringify([endpoint, jobsKey, revision]);
  const [time, setTime] = useState({ loading: false, records: null, errors: {}, error: '', scope: '' });
  useEffect(() => {
    const controller = new AbortController();
    const initial = { records: null, errors: {}, error: '', scope };
    if (projectLoading || projectError || jobsKey === '[]') {
      setTime({ ...initial, loading: false });
      return () => controller.abort();
    }
    setTime({ ...initial, loading: true });
    if (!endpoint) {
      setTime({ ...initial, loading: false, error: 'The Paylocity API URL is not configured.' });
      return () => controller.abort();
    }
    fetchPaylocityJobs(endpoint, JSON.parse(jobsKey), controller.signal).then(({ records, errors }) => {
      const failed = Object.keys(errors).length;
      if (!controller.signal.aborted) setTime({ ...initial, loading: false, records, errors,
        error: failed ? `Tracked hours could not be loaded for ${failed} job(s). Actual-hour totals exclude those jobs.` : '' });
    }).catch(error => {
      if (!controller.signal.aborted) setTime({ ...initial, loading: false, error: error.message || 'Unable to load tracked hours.' });
    });
    return () => controller.abort();
  }, [endpoint, jobsKey, scope, projectLoading, projectError]);

  const groups = useMemo(() => {
    const indexed = new Map();
    (time.scope === scope ? time.records || [] : []).forEach(record => {
      const code = String(record.jobs || '').trim().toUpperCase();
      if (!indexed.has(code)) indexed.set(code, []);
      indexed.get(code).push(record);
    });
    const result = { progress: [], complete: [], shipped: [] };
    projects.forEach(project => {
      const group = engineeringGroup(project);
      if (!group) return;
      const number = String(projectValue(project, 'JobNumber') || '').trim();
      let summary = null;
      let error = '';
      try {
        summary = summarizeEngineeringJob(project, indexed.get(`P${number.slice(2)}00`) || []);
        if (!summary) error = 'Suffix job — mapping needs review';
        else if (time.scope === scope && time.errors[toPaylocityJob(number)]) error = 'Tracked hours unavailable — refresh to retry';
      } catch { error = 'Invalid tracked hours'; }
      result[group].push({ project, number, summary, error });
    });
    ['progress', 'complete'].forEach(group => result[group].sort((a, b) => compareEngineeringShipDates(a.project, b.project)));
    result.shipped.sort((a, b) => a.number.localeCompare(b.number));
    return result;
  }, [projects, time, scope]);
  const visible = (groups[tab] || []).filter(row => `${row.number} ${projectValue(row.project, 'ProjectName') || ''}`.toLowerCase().includes(search.trim().toLowerCase()));
  const ready = time.scope === scope && time.records != null && !time.loading;
  const shipWeeks = ['progress', 'complete'].includes(tab)
    ? groupEngineeringShipWeeks(visible)
    : [{ key: 'shipped', rows: visible }];
  const totals = visible.reduce((sum, row) => {
    row.summary?.departments.forEach(department => {
      const previous = sum.departments[department.name] || {};
      if (department.allocated != null) previous.allocated = (previous.allocated ?? 0) + department.allocated;
      if (ready && department.actual != null) previous.actual = (previous.actual ?? 0) + department.actual;
      sum.departments[department.name] = previous;
    });
    ['allocated', 'engineering'].forEach(key => {
      const value = row.summary?.[key];
      if (value != null && (key === 'allocated' || ready)) sum[key] = (sum[key] ?? 0) + value;
    });
    return sum;
  }, { departments: {} });

  return <section aria-label="Engineering job overview" className="mb-4">
    <div className="d-flex justify-content-between align-items-center mb-3">
      <h4>Engineering job overview</h4>
      <Button variant="outline-primary" disabled={projectLoading || time.loading} onClick={() => { onRefreshProjects(); setRevision(value => value + 1); }}>Refresh jobs and hours</Button>
    </div>
    <p className="text-muted">In-progress jobs follow the job schedule's release and visibility filters and have at least one department with a due date and no completion date, with overall engineering unfinished. Complete, unshipped jobs have both due and done dates for mechanical and electrical engineering. Both tabs are grouped by Sunday–Saturday shipping week and ordered by planned ship date, with unscheduled jobs last. Department dates come from the full schedule. Allocated totals sum mechanical and electrical at 8 hours per day. Tracked totals include only mechanical and electrical engineering.</p>
    <Tabs activeKey={tab} onSelect={setTab} className="mb-3" id="engineering-time-tabs">
      <Tab eventKey="progress" title={`Engineering in progress (${groups.progress.length})`} />
      <Tab eventKey="complete" title={`Engineering complete · Not shipped (${groups.complete.length})`} />
      <Tab eventKey="shipped" title={`Shipped (${groups.shipped.length})`} />
      <Tab eventKey="search" title="Job search" />
    </Tabs>
    {tab === 'search' ? children : <>
    <Form.Group controlId="engineering-time-search" className="mb-3">
      <Form.Label>Filter jobs</Form.Label>
      <Form.Control value={search} onChange={event => setSearch(event.target.value)} placeholder="Job number or project name" />
    </Form.Group>
    {projectLoading && <p role="status">Loading projects…</p>}
    {time.loading && <p role="status">Loading accumulated hours…</p>}
    {time.error && <Alert variant="danger">{time.error} Refresh to retry.</Alert>}
    {!projectError && !projectLoading && <Table responsive bordered striped>
      <caption>Totals cover filtered jobs and known values only. Utilization = actual ÷ allocated: orange at 90–100%, red above 100%. Total utilization is shown only when every listed job has both values. * Allocation is incomplete. — means unavailable or no time reported. Hours are cumulative, not hours since engineering completion.</caption>
      <thead>
        <tr>
          <th rowSpan={2} scope="col">Job</th>
          <th rowSpan={2} scope="col">Project</th>
          <th rowSpan={2} scope="col">Model Number</th>
          {['Mechanical', 'Electrical'].map(name => <th key={name} colSpan={3} scope="colgroup" className="text-center">{name}</th>)}
          <th rowSpan={2} scope="col">Total hours</th>
          <th rowSpan={2} scope="col" className="text-end">Gross Margin %</th>
        </tr>
        <tr>{['Mechanical', 'Electrical'].map(name => <React.Fragment key={name}>
          <th scope="col" className="text-end">Allocated (h)</th>
          <th scope="col" className="text-end">Actual (h)</th>
          <th scope="col" className="text-end">Utilization</th>
        </React.Fragment>)}</tr>
      </thead>
      {shipWeeks.map(week => <tbody key={week.key}>
      {week.label && <tr><th colSpan={11} scope="rowgroup" style={{ backgroundColor: '#0055a5', color: 'white', fontWeight: 'bold', fontSize: '12pt', padding: '8px' }}>{week.label}</th></tr>}
      {week.rows.map(({ project, number, summary, error }, index) => <tr key={projectValue(project, 'ID') ?? `${number}-${index}`}>
        <th scope="row"><Button variant="link" className="p-0" disabled={!/^\d{7}$/.test(number)} onClick={event => { setTab('search'); onSelectJob(event, number); }}>{number || 'No job number'}</Button>{error && <small className="d-block text-danger">{error}</small>}</th>
        <td>{projectValue(project, 'ProjectName')}{tab === 'shipped' && <small className="d-block">Shipped: {String(projectValue(project, 'ActualShipDate') || '—').split('T')[0]}</small>}</td>
        <td>{projectValue(project, 'ModelNumber') || '—'}</td>
        {['Mechanical', 'Electrical'].map(name => {
          const department = summary?.departments.find(item => item.name === name);
          return <React.Fragment key={name}>
            <td className="text-end">{format(department?.allocated)}</td>
            <td className="text-end">{format(ready ? department?.actual : null)}</td>
            <UtilizationCell allocated={department?.allocated} actual={ready ? department?.actual : null} />
          </React.Fragment>;
        })}
        <td><div>Allocated: {format(summary?.allocated)} h{summary?.partial ? '*' : ''}</div><div>Actual: {format(ready ? summary?.engineering : null)} h</div></td>
        <td className="text-end">{formatMargin(projectValue(project, 'GrossMarginPercent'))}</td>
      </tr>)}</tbody>)}
      {visible.length === 0 && <tbody><tr><td colSpan={11}>No jobs match this view.</td></tr></tbody>}
      <tfoot className="table-light fw-bold"><tr><th scope="row">Totals ({visible.length} jobs)</th><td /><td />{['Mechanical', 'Electrical'].map(name => <React.Fragment key={name}>
        <td className="text-end">{format(totals.departments[name]?.allocated)}</td>
        <td className="text-end">{format(totals.departments[name]?.actual)}</td>
        <UtilizationCell allocated={totals.departments[name]?.allocated} actual={ready && visible.every(row => {
          const department = row.summary?.departments.find(item => item.name === name);
          return department?.allocated != null && department?.actual != null;
        }) ? totals.departments[name]?.actual : null} />
      </React.Fragment>)}<td><div>Allocated: {format(totals.allocated)} h</div><div>Actual: {format(totals.engineering)} h</div></td><td /></tr></tfoot>
    </Table>}
    </>}
  </section>;
}
